import {
  guestAskOf,
  instrumentVersionLabel,
  itemsAnsweredIn,
  placementNameAt,
  type Assessment,
  type CurrentUser,
  type ItemsAnswered,
  type PersonInvolvement,
  type PublishedInstrumentSchema,
} from "@thalamos/haven-common";

import {
  assessorNameOf,
  deciderNameOf,
} from "../Persons/involvementManagement.js";

type Submission = Assessment["submissions"][number];

/** An assessment, and the submission of it a summary shows. */
export type SummarisedAssessment = {
  assessment: Assessment;
  submission: Submission;
};

/**
 * The moment a summary describes an assessment as of: when it was approved,
 * when it was completed without needing approval, or otherwise, when its
 * latest attempt was submitted.
 */
export type SummaryMoment = {
  kind: "approval" | "completion" | "submission";
  at: Date;
};

/**
 * What one fact says: a value, `null` where there is none, or undefined where
 * it cannot be told, such as before what would tell it has been read.
 */
export type Told = string | null | undefined;

/**
 * One fact about the assessment, or about both in a comparison: the same in
 * each, or moved from the earlier one's to this one's.
 */
export type FactValue =
  | { kind: "one"; value: Told }
  | { kind: "both"; value: Told }
  | { kind: "moved"; from: Told; to: Told };

export type SummaryFacts = {
  /** This assessment's moment, after the earlier one's in a comparison. */
  moments: readonly [SummaryMoment] | readonly [SummaryMoment, SummaryMoment];
  placement: FactValue;
  assessor: FactValue;
  /** Absent where neither assessment needed approval. */
  approver?: FactValue | undefined;
  instrument: FactValue;
  /** Present where the assessment shown alone is a guest's. */
  guest?: GuestFacts | undefined;
};

/** What the bar says of a guest's assessment beyond whose it is. */
export type GuestFacts = {
  askerName: string;
  /** Undefined until the instrument is read, which says which answers are comments. */
  answered: ItemsAnswered | undefined;
};

export const momentOf = ({
  assessment,
  submission,
}: SummarisedAssessment): SummaryMoment => {
  if (submission.decision?.outcome === "approved") {
    return { kind: "approval", at: submission.decision.decidedAt };
  }

  return {
    kind: assessment.status === "completed" ? "completion" : "submission",
    at: submission.submittedAt,
  };
};

/**
 * What a summary says about the assessment it shows, and, in a comparison,
 * about the earlier one beside it.
 *
 * The placement is the one at each assessment's moment, and the approver is
 * whoever decided it, not whoever approves the assessor's work now.
 */
export const factsOf = ({
  current,
  previous,
  involvements,
  viewer,
  schema,
}: {
  current: SummarisedAssessment;
  previous?: SummarisedAssessment | undefined;
  involvements: PersonInvolvement[] | undefined;
  viewer: CurrentUser | undefined;
  /** The current assessment's pinned schema; undefined until read. */
  schema?: PublishedInstrumentSchema | undefined;
}): SummaryFacts => {
  const factOf = (
    read: (summarised: SummarisedAssessment) => Told,
  ): FactValue => {
    const to = read(current);

    if (previous === undefined) {
      return { kind: "one", value: to };
    }

    const from = read(previous);

    return from === to
      ? { kind: "both", value: to }
      : { kind: "moved", from, to };
  };
  const approverOf = ({ submission }: SummarisedAssessment) =>
    submission.decision?.outcome === "approved"
      ? deciderNameOf(involvements, submission.decision.decidedByUserId)
      : undefined;

  return {
    moments:
      previous === undefined
        ? [momentOf(current)]
        : [momentOf(previous), momentOf(current)],
    // Unknown until the involvements are read, which is not the same as none.
    placement: factOf((summarised) =>
      involvements === undefined
        ? undefined
        : placementNameAt({ involvements, at: momentOf(summarised).at }),
    ),
    assessor: factOf(({ assessment }) =>
      assessorNameOf(assessment, viewer, involvements),
    ),
    approver:
      current.assessment.definitive || previous?.assessment.definitive === true
        ? factOf(approverOf)
        : undefined,
    // Compared as practitioners name it, so two revisions of one version read as
    // the same: nobody chooses between revisions.
    instrument: factOf(({ assessment }) =>
      instrumentVersionLabel(assessment.instrument.name),
    ),
    guest:
      previous === undefined
        ? guestFactsOf({ current, involvements, schema })
        : undefined,
  };
};

const guestFactsOf = ({
  current: { assessment, submission },
  involvements,
  schema,
}: {
  current: SummarisedAssessment;
  involvements: PersonInvolvement[] | undefined;
  schema: PublishedInstrumentSchema | undefined;
}): GuestFacts | undefined => {
  const ask = guestAskOf(assessment, involvements);

  return ask === undefined
    ? undefined
    : {
        askerName: ask.askerName,
        answered:
          schema === undefined
            ? undefined
            : itemsAnsweredIn({ schema, responses: submission.responses }),
      };
};
